import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { CaretDown, Upload, FileXls, FilePdf, PencilSimple, GoogleLogo, Trash, TrashSimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

/**
 * Actions dropdown used on Customers & Products pages.
 * Props:
 *   onImport, onDownloadExcel, onDownloadPdf, onBulkEdit, onDownloadCsv,
 *   onBulkDelete, onDeleteAll
 *   entityLabel: "Customers" | "Products"
 *   selectedCount: number of currently selected rows
 */
export default function ActionsMenu({
  onImport, onDownloadExcel, onDownloadPdf, onBulkEdit, onDownloadCsv,
  onBulkDelete, onDeleteAll, entityLabel = "Items", selectedCount = 0, canDestructive = false,
  canManage = true,
}) {
  const [open, setOpen] = useState(false);
  const [confirmType, setConfirmType] = useState(null); // "bulk" | "all"
  const [chk, setChk] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openConfirm = (type) => { setConfirmType(type); setChk(false); setOpen(false); };
  const closeConfirm = () => { setConfirmType(null); setChk(false); };

  const doConfirm = () => {
    if (!chk) return;
    const t = confirmType;
    closeConfirm();
    if (t === "bulk") onBulkDelete?.();
    else if (t === "all") onDeleteAll?.();
  };

  const Item = ({ icon: Icon, label, onClick, destructive, disabled, testId }) => (
    <button
      onClick={() => { if (!disabled) { setOpen(false); onClick?.(); } }}
      disabled={disabled}
      data-testid={testId}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors
        ${disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-100 dark:hover:bg-slate-800"}
        ${destructive ? "text-red-600 dark:text-red-400 font-medium" : ""}`}
    >
      <Icon size={18} />
      <span>{label}</span>
    </button>
  );

  return (
    <div className="relative inline-block" ref={ref}>
      <Button variant="outline" onClick={() => setOpen((o) => !o)} data-testid="actions-menu-btn" className="gap-1.5">
        Actions <CaretDown size={14} />
      </Button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg z-30 py-1"
          data-testid="actions-menu"
        >
          {canManage && <Item icon={Upload} label="Import" onClick={onImport} testId="action-import" />}
          <Item icon={FileXls} label="Download as Excel" onClick={onDownloadExcel} testId="action-download-excel" />
          <Item icon={FilePdf} label="Download as PDF" onClick={onDownloadPdf} testId="action-download-pdf" />
          <Item icon={PencilSimple} label={`Bulk Edit${selectedCount ? ` (${selectedCount})` : ""}`} onClick={onBulkEdit} disabled={selectedCount === 0} testId="action-bulk-edit" />
          <Item icon={GoogleLogo} label="Export to Google Sheets" onClick={onDownloadCsv} testId="action-export-gsheets" />
          {canDestructive && (
            <>
              <div className="border-t border-slate-200 dark:border-slate-700 my-1" />
              <Item icon={TrashSimple} label={`Bulk Delete${selectedCount ? ` (${selectedCount})` : ""}`} destructive disabled={selectedCount === 0} onClick={() => openConfirm("bulk")} testId="action-bulk-delete" />
              <Item icon={Trash} label={`Delete All ${entityLabel}`} destructive onClick={() => openConfirm("all")} testId="action-delete-all" />
            </>
          )}
        </div>
      )}

      <Dialog open={!!confirmType} onOpenChange={(o) => !o && closeConfirm()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {confirmType === "bulk" ? `Delete ${selectedCount} selected ${entityLabel.toLowerCase()}?` : `Delete ALL ${entityLabel.toLowerCase()}?`}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 dark:text-slate-300 space-y-3">
            <p>
              {confirmType === "bulk"
                ? `This will remove the ${selectedCount} selected row(s). This action can only be reversed by Super Admin from the deleted/archive view.`
                : `This will remove EVERY ${entityLabel.toLowerCase().replace(/s$/, "")} in your database. This is irreversible from the UI. Export a backup first.`}
            </p>
            <label className="flex items-start gap-2 cursor-pointer select-none p-3 border border-red-300 rounded-md bg-red-50 dark:bg-red-950/30">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-red-600"
                checked={chk}
                onChange={(e) => setChk(e.target.checked)}
                data-testid="confirm-delete-checkbox"
              />
              <span className="text-sm">
                I understand this will permanently remove {confirmType === "bulk" ? `the ${selectedCount} selected ${entityLabel.toLowerCase()}` : `ALL ${entityLabel.toLowerCase()}`} from the system.
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeConfirm}>Cancel</Button>
            <Button disabled={!chk} onClick={doConfirm} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">
              {confirmType === "bulk" ? `Delete ${selectedCount}` : `Delete All`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
